import { useRef, useEffect, useCallback, type ChangeEvent, type KeyboardEvent, type RefCallback } from 'react';
import { CARD_READER_DEBOUNCE_MS } from '@/shared/config/terminal.config';

/**
 * useCardReader — captures HID card reader input via a hidden input element.
 *
 * Focus trap strategy:
 * - Ref callback (setInputRef): attaches `focusout` listener DIRECTLY to the DOM
 *   node every time it mounts. `focusout` fires reliably even when focus goes to
 *   BODY (unlike `focusin` on document). On focusout, refocus via setTimeout(0).
 * - pointerdown capture: prevents focusable elements (buttons) from stealing focus.
 * - keydown fallback: captures chars while focus is briefly on BODY.
 * - window.focus: reclaims after app switching.
 *
 * Why ref callback instead of useEffect + inputRef.current?
 *   useEffect captures inputRef.current at the time it runs. If React re-renders
 *   and recreates the input element, the listener is attached to the OLD node.
 *   RefCallback fires with the actual DOM node on every mount/unmount.
 */
export function useCardReader(
  onSubmit: (value: string) => void,
  debounceMs: number = CARD_READER_DEBOUNCE_MS,
) {
  const inputRef       = useRef<HTMLInputElement | null>(null);
  const unmountedRef   = useRef(false);
  const timerRef       = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const accumulatedRef = useRef('');

  const fireDebounce = useCallback((val: string) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const current = accumulatedRef.current;
      if (current) {
        accumulatedRef.current = '';
        if (inputRef.current) inputRef.current.value = '';
        onSubmit(current);
      }
    }, debounceMs);
    void val;
  }, [onSubmit, debounceMs]);

  const refocus = useCallback(() => {
    if (!unmountedRef.current && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, []);

  // Global listeners — document/window level
  useEffect(() => {
    unmountedRef.current = false;

    // Block buttons/links from stealing focus on click
    const onPointerDown = (e: PointerEvent) => {
      if (unmountedRef.current) return;
      if (e.target !== inputRef.current) {
        e.preventDefault();
        setTimeout(() => { refocus(); }, 0);
      }
    };

    // Reclaim after switching apps/tabs
    const onWindowFocus = () => { setTimeout(() => { refocus(); }, 0); };

    // Capture chars while focus is briefly on BODY
    const onDocKeyDown = (e: globalThis.KeyboardEvent) => {
      if (unmountedRef.current) return;
      if (e.target === inputRef.current) return; // already handled by onChange
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        accumulatedRef.current += e.key;
        if (inputRef.current) {
          const setter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value',
          )?.set;
          if (setter) {
            setter.call(inputRef.current, accumulatedRef.current);
            inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            inputRef.current.value = accumulatedRef.current;
          }
        }
        fireDebounce(accumulatedRef.current);
        refocus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown',     onDocKeyDown,  true);
    window.addEventListener('focus',         onWindowFocus);

    return () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown',     onDocKeyDown,  true);
      window.removeEventListener('focus',         onWindowFocus);
    };
  }, [refocus, fireDebounce]);

  // Ref callback — attaches focusout directly to the live DOM node.
  // More reliable than useEffect snapshot because it fires with the actual node.
  const setInputRef: RefCallback<HTMLInputElement> = useCallback((node) => {
    // Remove listener from previous node
    const prev = inputRef.current as (HTMLInputElement & { __focusOutHandler?: () => void }) | null;
    if (prev?.__focusOutHandler) {
      prev.removeEventListener('focusout', prev.__focusOutHandler);
      delete prev.__focusOutHandler;
    }

    inputRef.current = node;

    if (node && !unmountedRef.current) {
      node.focus({ preventScroll: true });
      const onFocusOut = () => {
        if (!unmountedRef.current) {
          setTimeout(() => { refocus(); }, 0);
        }
      };
      (node as HTMLInputElement & { __focusOutHandler?: () => void }).__focusOutHandler = onFocusOut;
      node.addEventListener('focusout', onFocusOut);
    }
  }, [refocus]);

  // React onBlur safety net
  const handleBlur = useCallback(() => {
    requestAnimationFrame(() => {
      if (!unmountedRef.current) refocus();
    });
  }, [refocus]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      accumulatedRef.current = e.target.value;
      fireDebounce(accumulatedRef.current);
    },
    [fireDebounce],
  );

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.preventDefault();
  }, []);

  return { inputRef: setInputRef, handleBlur, handleChange, handleKeyDown };
}
